import { useAuth } from "@/context/auth";
import { useLazyAppKit } from "@/lib/lazy-appkit";

/**
 * Hook that returns a handler wrapper function.
 * If user is not connected, opens the wallet connection modal.
 * If wallet is connected but user is not logged in, opens the login modal.
 * If both connected and logged in, executes the provided function.
 *
 * @example
 * const handleLoginRequired = useLoginRequired();
 *
 * <Button onClick={handleLoginRequired(() => {
 *   // Your custom function here
 *   console.log("User is logged in!");
 * })}>
 *   Click me
 * </Button>
 */
export const useLoginRequired = () => {
  const { open: openAppKit } = useLazyAppKit();
  const { isLoggedIn } = useAuth();

  const handleLoginRequired = <T extends (...args: unknown[]) => unknown>(
    fn: T,
  ): T => {
    return ((...args: Parameters<T>) => {
      if (!isLoggedIn) {
        openAppKit();
        return;
      }

      return fn(...args);
    }) as T;
  };

  return handleLoginRequired;
};
